import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Heart, Users, Home, Phone, Mail, MapPin, Shield, Clock, Award } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Capture referral code from URL and store it
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('referral_code', refCode.toUpperCase());
      console.log('Referral code captured:', refCode);
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation - Premium Style */}
      <nav className="nav-premium sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-10">
              <img 
                src="https://customer-assets.emergentagent.com/job_caregiver-command/artifacts/prj1fr46_Integrity%20Home%20Care%20Logo%20Webpage.png"
                alt="Integrity Home Care LLC"
                className="h-14 w-auto"
              />
              <div className="hidden md:flex items-center gap-8">
                <a href="#home" className="text-gray-600 hover:text-purple-700 font-medium transition-all duration-200 text-sm">Home</a>
                <a href="#about" className="text-gray-600 hover:text-purple-700 font-medium transition-all duration-200 text-sm">About Us</a>
                <a href="#services" className="text-gray-600 hover:text-purple-700 font-medium transition-all duration-200 text-sm">Services</a>
                <a href="#team" className="text-gray-600 hover:text-purple-700 font-medium transition-all duration-200 text-sm">Our Team</a>
                <a href="#contact" className="text-gray-600 hover:text-purple-700 font-medium transition-all duration-200 text-sm">Contact</a>
                <a href="/careers" className="text-gray-600 hover:text-purple-700 font-medium transition-all duration-200 text-sm">Join Our Team</a>
              </div>
            </div>
            <Button
              onClick={() => navigate('/portal')}
              className="btn-primary"
              data-testid="staff-portal-button"
            >
              Staff Portal
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Enhanced */}
      <section id="home" className="relative bg-gradient-subtle py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-purple-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-purple-300 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-10">
              <div>
                <h1 className="text-5xl lg:text-6xl font-bold text-purple-900 mb-6 leading-tight tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Your Loved Ones,<br />Our Family
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Compassionate, professional home care services rooted in faith and dedicated to serving with integrity.
                </p>
              </div>

              <div className="card-glass p-6 border-l-4 border-purple-600">
                <p className="text-purple-900 italic text-lg font-medium leading-relaxed">
                  "Even as the Son of man came not to be ministered unto, but to minister, and to give his life a ransom for many."
                </p>
                <p className="text-purple-600 text-sm mt-3 font-semibold">— Matthew 20:28</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="btn-primary text-lg px-8 py-6"
                  data-testid="request-care-button"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Request Care
                </Button>
                <Button
                  onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 text-lg px-8 py-6 transition-all duration-200"
                >
                  Learn More
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758686254563-5c5ab338c8b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwaGFwcHklMjBob21lfGVufDB8fHx8MTc3MzI0MDE0NHww&ixlib=rb-4.1.0&q=85"
                  alt="Compassionate caregiving"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 card-premium p-6">
                <div className="flex items-center gap-4">
                  <div className="icon-container">
                    <Heart className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-900">15+</p>
                    <p className="text-sm text-gray-500 font-medium">Years of Care</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us / Mission Section - Enhanced */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl mb-6">Our Mission</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Integrity Home Care LLC will minister to the community with a servant's heart, providing compassionate care through home, community, respite, and nursing care services to individuals. We will provide day and vocational programing through supported employment and community experience. Integrity Home Care LLC will honor the dignity of every individual, as we follow the example of Christ who came not to be ministered unto, but to minister (Matthew 20:28).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card-stat text-center cursor-default">
              <div className="flex justify-center mb-6">
                <div className="icon-container p-5">
                  <Shield className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-purple-900 mb-4">Integrity First</h3>
              <p className="text-gray-600 leading-relaxed">
                We uphold the highest standards of honesty, transparency, and ethical care in everything we do.
              </p>
            </div>

            <div className="card-stat text-center cursor-default">
              <div className="flex justify-center mb-6">
                <div className="icon-container p-5">
                  <Heart className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-purple-900 mb-4">Compassionate Care</h3>
              <p className="text-gray-600 leading-relaxed">
                Every client is treated with the dignity, respect, and love they deserve as part of our extended family.
              </p>
            </div>

            <div className="card-stat text-center cursor-default">
              <div className="flex justify-center mb-6">
                <div className="icon-container p-5">
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-purple-900 mb-4">Professional Excellence</h3>
              <p className="text-gray-600 leading-relaxed">
                Our trained caregivers bring expertise, dedication, and a servant's heart to every interaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Enhanced */}
      <section id="services" className="py-24 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl mb-6">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive home care solutions tailored to meet the unique needs of each individual and family.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Home, title: 'Personal Care', description: 'Assistance with daily living activities including bathing, dressing, grooming, and mobility.' },
              { icon: Heart, title: 'Companionship', description: 'Emotional support, conversation, and meaningful engagement to combat loneliness.' },
              { icon: Clock, title: 'Respite Care', description: 'Temporary relief for family caregivers, allowing them time to rest and recharge.' },
              { icon: Users, title: 'Community Services', description: 'Day and vocational programming through supported employment and community experience.' },
              { icon: Shield, title: 'Nursing Care', description: 'Professional nursing care services delivered with compassion and clinical excellence.' },
              { icon: Award, title: 'Home Support', description: 'Comprehensive home care services to help maintain independence and quality of life.' },
            ].map((service, index) => (
              <div key={index} className="card-premium p-6 group">
                <div className="icon-container w-fit mb-5">
                  <service.icon className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-purple-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Temporarily Hidden Until We Have Real Testimonials */}
      {/* 
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-purple-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              What Families Say
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Hear from families we've had the privilege to serve
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'The Johnson Family',
                quote: 'Integrity Home Care has been a blessing to our family. Their caregivers treat our mother with such dignity and respect. We are grateful for their Christ-centered approach.',
                service: 'Personal Care Services'
              },
              {
                name: 'Sarah Mitchell',
                quote: 'The team at Integrity truly lives up to their name. They have been compassionate and professional, always going above and beyond for my father.',
                service: 'Respite Care'
              },
              {
                name: 'The Davis Family',
                quote: 'We feel blessed to have found Integrity Home Care. Their servant hearts shine through in everything they do. Our loved one is in wonderful hands.',
                service: 'Home Companion Services'
              }
            ].map((testimonial, index) => (
              <Card key={index} className="p-8 border-purple-100 relative">
                <div className="absolute top-6 left-6 text-6xl text-purple-200 font-serif">"</div>
                <div className="relative z-10 pt-8">
                  <p className="text-gray-700 italic mb-6 leading-relaxed">
                    {testimonial.quote}
                  </p>
                  <div className="border-t border-purple-100 pt-4">
                    <p className="font-bold text-purple-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.service}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Meet Our Team Section - Enhanced */}
      <section id="team" className="py-24 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl mb-6">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dedicated professionals committed to serving with a servant's heart
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: 'Laura & Jonathan Vasaturo',
                title: 'Owners',
                bio: 'Committed to honoring the dignity of every individual through compassionate, Christ-centered care.'
              },
              {
                name: 'Jonathan Vasaturo',
                credentials: 'MBA, BSN, RN, Medsurge-BC',
                title: 'Director & Nursing Supervisor',
                bio: 'Leading with clinical excellence and a heart for ministry in healthcare.'
              },
              {
                name: 'Theresa Cianciolo',
                credentials: 'DMin, MS, MA, AEMT',
                title: 'Assistant Director',
                bio: 'Bringing theological insight and emergency medical expertise to compassionate care.'
              },
              {
                name: 'Scott Cianciolo',
                credentials: 'DMin, MEd',
                title: 'Home Companion',
                bio: 'Providing meaningful companionship with ministerial compassion and educational insight.'
              }
            ].map((member, index) => (
              <div key={index} className="card-premium p-6 text-center group">
                <div className="mb-5 flex justify-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Users className="w-10 h-10 text-purple-600" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-purple-900 mb-1">{member.name}</h3>
                {member.credentials && (
                  <p className="text-xs text-purple-600 font-semibold mb-2">{member.credentials}</p>
                )}
                <p className="text-sm font-semibold text-gray-600 mb-3">{member.title}</p>
                <p className="text-sm text-gray-500 leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section - Enhanced */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="section-title text-4xl mb-6">Get In Touch</h2>
              <p className="text-xl text-gray-600 mb-10">
                We're here to answer your questions and help you find the right care solution for your loved ones.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-5">
                  <div className="icon-container">
                    <Phone className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-purple-900 mb-1">Phone</p>
                    <a href="tel:+15716062347" className="text-gray-600 hover:text-purple-600 transition-colors">
                      (571) 606-2347
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="icon-container">
                    <Mail className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-purple-900 mb-1">Email</p>
                    <a href="mailto:info@integrityhomecarenc.com" className="text-gray-600 hover:text-purple-600 transition-colors">
                      info@integrityhomecarenc.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="icon-container">
                    <MapPin className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-purple-900 mb-1">Service Areas</p>
                    <p className="text-gray-600">
                      Brunswick, New Hanover, Columbus, Pender, Robeson, Bladen, Cumberland, Sampson, Duplin, Onslow Counties
                    </p>
                    <p className="text-purple-600 font-medium mt-2">
                      www.IntegrityHomeCareNC.com
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-premium p-8">
              <h3 className="text-2xl font-bold text-purple-900 mb-6">Request Care</h3>
              <form className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-purple-900 mb-2">Full Name *</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-purple-900 mb-2">Email *</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-purple-900 mb-2">Phone *</label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-purple-900 mb-2">Message</label>
                  <textarea
                    rows="4"
                    className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Tell us about your care needs..."
                  ></textarea>
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary py-6 text-lg"
                  data-testid="submit-contact-form"
                >
                  Send Message
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Enhanced */}
      <footer className="bg-purple-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 bg-white/10 rounded-xl backdrop-blur-sm">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <span className="text-xl font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Integrity Home Care LLC
                </span>
              </div>
              <p className="text-purple-200 leading-relaxed">
                Compassionate care rooted in faith and integrity.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-5">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="#home" className="text-purple-200 hover:text-white transition-colors">Home</a></li>
                <li><a href="#about" className="text-purple-200 hover:text-white transition-colors">About Us</a></li>
                <li><a href="#services" className="text-purple-200 hover:text-white transition-colors">Services</a></li>
                <li><a href="#team" className="text-purple-200 hover:text-white transition-colors">Our Team</a></li>
                <li><a href="#contact" className="text-purple-200 hover:text-white transition-colors">Contact</a></li>
                <li><a href="/careers" className="text-purple-200 hover:text-white transition-colors">Join Our Team</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-5">Contact</h4>
              <ul className="space-y-3 text-purple-200">
                <li>Phone: (571) 606-2347</li>
                <li>Email: info@integrityhomecarenc.com</li>
                <li>www.IntegrityHomeCareNC.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-purple-700 pt-8 text-center text-purple-300">
            <p>© 2024 Integrity Home Care LLC. All rights reserved.</p>
            <p className="mt-3 italic text-sm">"Even as the Son of man came not to be ministered unto, but to minister." - Matthew 20:28</p>
          </div>
        </div>
      </footer>
    </div>
  );
}